import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "@/components/common/surfaceClasses";
import { AudienceDataDestinationHelpMenu } from "@/components/audience-data/AudienceDataDestinationHelpMenu";
import { AudienceDataExportPreview } from "@/components/audience-data/AudienceDataExportPreview";
import { AudienceDataHostedFeedCard } from "@/components/audience-data/AudienceDataHostedFeedCard";
import type { SsdDestinationFeed, SsdExportJob, SsdGoogleConversions } from "@/lib/ssdSalesClient";
import type { SsdExportDestination } from "@/lib/ssd/ssdExportPreview";

export function AudienceDataExportCard(props: {
  exporting: "google" | "meta" | null;
  latestExport: SsdExportJob | null;
  conversions: SsdGoogleConversions;
  savingConversions: boolean;
  onSelectConversion: (name: string) => void;
  onAddConversion: (name: string) => void;
  onExport: (destination: "google" | "meta") => void;
  hostedFeeds: SsdDestinationFeed[];
  hostedTokens: Partial<Record<"google" | "meta", string>>;
  rotatingFeed: "google" | "meta" | null;
  onRotateFeed: (destination: "google" | "meta") => void;
}) {
  const [preview, setPreview] = useState<SsdExportDestination | null>(null);

  return (
    <SectionCard
      className="min-w-0 overflow-hidden"
      title="Destinations"
      description="Manual download, format preview, and hosted pull URLs for Google Ads Data Manager / Meta. Email and phone are SHA-256 hashed."
      rightSlot={<AudienceDataDestinationHelpMenu />}
    >
      <div className="flex min-w-0 flex-wrap gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          disabled={props.exporting !== null}
          onClick={() => props.onExport("google")}
        >
          {props.exporting === "google" ? "Building…" : "Download Google CSV"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={props.exporting !== null}
          onClick={() => props.onExport("meta")}
        >
          {props.exporting === "meta" ? "Building…" : "Download Meta CSV"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          aria-expanded={preview !== null}
          onClick={() => setPreview((v) => (v ? null : "google"))}
        >
          {preview ? "Hide preview" : "Format preview"}
        </button>
      </div>
      {preview ? (
        <AudienceDataExportPreview
          destination={preview}
          onDestination={setPreview}
          conversions={props.conversions}
          savingConversions={props.savingConversions}
          onSelectConversion={props.onSelectConversion}
          onAddConversion={props.onAddConversion}
        />
      ) : null}
      {props.latestExport ? (
        <p className="text-sm text-gray-600 dark:text-zinc-300">
          Last export: {props.latestExport.filename} · {props.latestExport.row_count} row
          {props.latestExport.row_count === 1 ? "" : "s"} · {props.latestExport.destination}
        </p>
      ) : (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No exports yet.</p>
      )}
      <AudienceDataHostedFeedCard
        feeds={props.hostedFeeds}
        tokens={props.hostedTokens}
        rotating={props.rotatingFeed}
        onRotate={props.onRotateFeed}
      />
    </SectionCard>
  );
}
